import type { MailboxFolder } from "@/models/MailboxConnection";
import type { HiddenFolder, MailFolderConnection, MailFolderCount, MailViewFolder } from "@/models/MailFolder";
import { buildFolderTree, type FolderNode } from "@/views/settings/email/connections/lib/folderTree";

type Translate = (key: string, values?: Record<string, unknown>) => string;

/** `<connection_id>:<folder_id>` — the `folder_ref` filter of the list and the key of every per-folder map. */
export function folderRef(connectionId: string, folderId: string): string {
  return connectionId + ":" + folderId;
}

/** Splits a `folder_ref` at the first colon (folder ids may contain colons, connection ids are UUIDs). */
export function parseFolderRef(ref: string): HiddenFolder | null {
  const at = ref.indexOf(":");
  if (at <= 0 || at === ref.length - 1) return null;
  return { connection_id: ref.slice(0, at), folder_id: ref.slice(at + 1) };
}

/** A folder of the viewer tree; `ref` is its `folder_ref`. */
export interface ViewFolderNode {
  ref: string;
  id: string;
  label: string;
  folder: MailViewFolder;
  children: ViewFolderNode[];
}

/**
 * The connection's folders as a tree. Reuses the settings tree builder (parent_id hierarchy, cycle
 * safety, role order and translated well-known names), so the viewer orders folders exactly like
 * the sync selection does.
 */
export function buildViewTree(connection: MailFolderConnection, t: Translate): ViewFolderNode[] {
  const byId = new Map(connection.folders.map((f) => [f.id, f]));
  const asCatalogue: MailboxFolder[] = connection.folders.map((f) => ({
    id: f.id,
    name: f.name,
    path: f.path,
    parent_id: f.parent_id,
    role: f.role,
    // Every viewer folder ranks as a normal folder; the "never synced" rank of the settings tree
    // would push synced: false ancestors to the end of their level.
    selectable: true,
    selected: f.synced !== false,
    synced_at: null,
    progress: { phase: "idle", total: null, done: 0, percent: null },
  }));
  const convert = (nodes: FolderNode[]): ViewFolderNode[] =>
    nodes.map((n) => ({
      ref: folderRef(connection.connection_id, n.id),
      id: n.id,
      label: n.label,
      folder: byId.get(n.id) as MailViewFolder,
      children: convert(n.children),
    }));
  return convert(buildFolderTree(asCatalogue, t));
}

export type HiddenState = "visible" | "own" | "inherited";

/**
 * Visibility of every folder: `own` when the user hid it, `inherited` when an ancestor is hidden
 * (its eye is disabled), else `visible`. Keyed by `folder_ref`.
 */
export function hiddenStates(roots: ViewFolderNode[], hidden: Set<string>): Map<string, HiddenState> {
  const out = new Map<string, HiddenState>();
  const walk = (nodes: ViewFolderNode[], ancestorHidden: boolean) => {
    for (const n of nodes) {
      const own = hidden.has(n.ref);
      out.set(n.ref, own ? "own" : ancestorHidden ? "inherited" : "visible");
      walk(n.children, ancestorHidden || own);
    }
  };
  walk(roots, false);
  return out;
}

/** Counts per `folder_ref`. */
export function countMap(counts: MailFolderCount[]): Map<string, number> {
  return new Map(counts.map((c) => [folderRef(c.connection_id, c.folder_id), c.count]));
}

/** The folder's own count, plus all descendants when `withSubtree` (a collapsed folder). */
export function folderCount(node: ViewFolderNode, counts: Map<string, number>, withSubtree: boolean): number {
  const own = counts.get(node.ref) ?? 0;
  if (!withSubtree) return own;
  return node.children.reduce((sum, child) => sum + folderCount(child, counts, true), own);
}

/** One rendered row of the sidebar tree. */
export interface FolderRow {
  node: ViewFolderNode;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
  state: HiddenState;
  count: number;
}

/**
 * The rows to render: depth-first, children of a collapsed folder left out. A collapsed folder
 * shows the sum of its subtree, an expanded one only its own mails.
 */
export function visibleRows(
  roots: ViewFolderNode[],
  collapsed: Set<string>,
  states: Map<string, HiddenState>,
  counts: Map<string, number>,
): FolderRow[] {
  const rows: FolderRow[] = [];
  const walk = (nodes: ViewFolderNode[], depth: number) => {
    for (const node of nodes) {
      const hasChildren = node.children.length > 0;
      const expanded = hasChildren && !collapsed.has(node.ref);
      rows.push({
        node,
        depth,
        hasChildren,
        expanded,
        state: states.get(node.ref) ?? "visible",
        count: folderCount(node, counts, hasChildren && !expanded),
      });
      if (expanded) walk(node.children, depth + 1);
    }
  };
  walk(roots, 0);
  return rows;
}

/** Finds a folder by its ref in a forest. */
export function findFolder(roots: ViewFolderNode[], ref: string): ViewFolderNode | null {
  for (const n of roots) {
    if (n.ref === ref) return n;
    const inChild = findFolder(n.children, ref);
    if (inChild) return inChild;
  }
  return null;
}

const COLLAPSED_KEY = "mail-view-collapsed";

// The collapse state is a per-browser convenience; a blocked storage only loses it.
export function loadCollapsed(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? "[]");
    return new Set(Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set();
  }
}

export function saveCollapsed(keys: Set<string>): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...keys]));
  } catch {
    /* a blocked storage only loses the preference */
  }
}

/** Forgets the collapse state (logout, account switch: the next user has other mailboxes). */
export function clearCollapsed(): void {
  try {
    localStorage.removeItem(COLLAPSED_KEY);
  } catch {
    /* nothing stored */
  }
}
